import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'
import { t } from 'ttag'

import { Accordion } from 'components/shared/Accordion/Accordion'
import { useLanguage } from 'hooks'
import { percentScore, formatTimeVerbose } from 'utils/stringFormat'
import { translateTrait, translateIntro, scoreBucket } from './descriptions'
import styles from './ScoreCard.module.scss'

export const TRAITS = {
  GMA: [],
  Agreeableness: ['A1', 'A2', 'A3', 'A4', 'A5', 'A6'],
  Conscientiousness: ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'],
  Extraversion: ['E1', 'E2', 'E3', 'E4', 'E5', 'E6'],
  'Openness to Experience': ['O1', 'O2', 'O3', 'O4', 'O5', 'O6'],
  'Emotional Stability': ['N1', 'N2', 'N3', 'N4', 'N5', 'N6'],
  Hardiness: ['H1', 'H2', 'H3'],
}

export const TRAIT_DESCRIPTIONS = {
  PA: {
    en:
      'Positive affectivity refers to a tendency to experience feelings of enthusiasm and engagement related to their work.',
    nb:
      'Positiv affektivitet refererer til en tendens til å kjenne på følelser av entusiasme og engasjement knyttet til arbeidet sitt.',
  },
  'NA Negative Affectivity': {
    en:
      'Negative affectivity refers to a tendency to experience feelings such as stress and nervousness related to their work.',
    nb:
      'Negativ affektivitet referer til en tendens til å kjenne på følelser som stress og nervøsitet knyttet til arbeidet sitt.',
  },
  Reliability: {
    en:
      'The Integrity score is a single indicator created by combining answers that tap honesty, self-control, diligence, and respectful behaviour across several Big-Five personality dimensions.',
    nb:
      'Integritetsskåren er en samlet indikator som kombinerer svar som fanger opp ærlighet, selvkontroll, arbeidsomhet og respektfull atferd på tvers av flere Big Five-personlighetsdimensjoner.',
  },
}

const ScoreCardContext = React.createContext({
  scores: {},
  stats: {},
  language: 'en',
  showScore: true,
  showScoreLabel: true,
  showIntro: true,
  isInitiallyOpen: false,
  separateTraits: false,
})

const Score = ({ className = '', value }) => (
  <span className={cx(styles.score, className)}>{percentScore(value)}/100</span>
)

const ScoreLabel = ({ value }) => (
  <span className={cx(styles.scoreLabel)}>{scoreBucket(value)[1]}</span>
)

const GmaStats = ({ isCandidate }) => {
  const { stats } = React.useContext(ScoreCardContext)
  const { correct, totalQuestions, totalAnswers, seconds } = stats.gma || {}

  return (
    <p>
      {isCandidate
        ? t`You answered ${totalAnswers} of ${totalQuestions} questions and used`
        : t`The candidate answered ${totalAnswers} of ${totalQuestions} questions and used`}{' '}
      {formatTimeVerbose(seconds)}.{' '}
      {isCandidate
        ? t`You had ${correct} correct answers.`
        : t`The candidate had ${correct} correct answers.`}
    </p>
  )
}

const Factor = ({
  facetsClassName = undefined,
  factorClassName = undefined,
  trait,
  facets = [],
  isFacet = false,
  isCandidate = true,
}) => {
  const {
    isInitiallyOpen,
    language,
    showScore,
    showScoreLabel,
    separateTraits,
    scores,
  } = React.useContext(ScoreCardContext)

  const score = R.prop(trait, scores)

  if (score === undefined) return null

  const { label = trait, abstract = [], description = [] } = translateTrait(
    trait,
    score,
    language,
    isCandidate,
  )

  return (
    <section
      className={cx(styles.factor, {
        [styles.withPadding]: separateTraits,
        [factorClassName]: Boolean(factorClassName),
      })}
    >
      <Accordion className={styles.accordion} isInitiallyOpen={isInitiallyOpen}>
        <Accordion.Header
          buttonClassName={cx(styles.button, {
            [styles.highlight]: separateTraits,
            [styles.facetButton]: isFacet,
          })}
          className={styles.header}
          wrapperClassName={cx({ [styles.wrapper]: isFacet })}
          title={
            <span
              className={cx(styles.title, { [styles.facetTitle]: isFacet })}
            >
              <span>{label}</span>
              {showScore && (
                <Score
                  className={cx({ [styles.result]: isFacet })}
                  value={score}
                />
              )}
              {showScoreLabel && !isFacet && <ScoreLabel value={score} />}
            </span>
          }
        />
        <Accordion.Panel>
          <div className={styles.panel}>
            {trait === 'GMA' && <GmaStats isCandidate={isCandidate} />}
            {abstract.map((text, idx) => (
              <p key={idx}>{text}</p>
            ))}
            {description.map((text, idx) => (
              <p className={styles.description} key={idx}>
                {text}
              </p>
            ))}
            {facets.length > 0 && (
              <>
                <p
                  className={styles.facetLabel}
                >{t`${label} has ${facets.length} sub facets:`}</p>
                <div
                  className={cx(styles.facets, {
                    [facetsClassName]: Boolean(facetsClassName),
                  })}
                >
                  {facets.map(trait => (
                    <Factor
                      key={trait}
                      trait={trait}
                      isFacet
                      isCandidate={isCandidate}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </Accordion.Panel>
      </Accordion>
    </section>
  )
}

const ScoreCardLede = ({ className = undefined, isCandidate = true }) => {
  const { language, showIntro } = React.useContext(ScoreCardContext)

  return (
    showIntro && (
      <div
        className={cx(styles.scoreCardLede, {
          [className]: Boolean(className),
        })}
      >
        {translateIntro(language, isCandidate).map((section, idx) =>
          isCandidate ? (
            <p children={section} key={idx} />
          ) : (
            <div key={idx}>
              <div className={styles.scoreCardTitle}>{section.title}</div>
              <p children={section.paragraphs} />
            </div>
          ),
        )}
      </div>
    )
  )
}

export const ScoreCard = ({
  facetsClassName,
  factorClassName,
  introClassName,
  scores = {},
  stats = {},
  showScore = false,
  showScoreLabel = false,
  showIntro = true,
  isInitiallyOpen = false,
  separateTraits = false,
  isCandidate = true,
}) => {
  const language = useLanguage()

  return (
    <ScoreCardContext.Provider
      value={{
        language,
        scores,
        stats,
        showIntro,
        showScore,
        isInitiallyOpen,
        showScoreLabel,
        separateTraits,
      }}
    >
      <ScoreCardLede
        className={introClassName}
        show={showIntro}
        isCandidate={isCandidate}
      />
      <div className={styles.scoreCard}>
        {Object.entries(TRAITS).map(([factor, facets]) => (
          <Factor
            key={factor}
            trait={factor}
            facets={facets}
            {...{ isCandidate, facetsClassName, factorClassName }}
          />
        ))}
      </div>
    </ScoreCardContext.Provider>
  )
}
